import {FACTION_CONFIGS_IMPLEMENTED, FACTION_IDS_IMPLEMENTED} from "../../configs/factions";
import {ALL_FACTION_SKINS} from "../../configs/factions/skins/allFactionSkins";
import {FACTION_UNIT_SKIN_CONFIGS} from "../../configs/factionUnitsSkins";
import {replaceColorsInObject} from "../other/replaceColorsInObject";
import {getRandomElement} from "../other/getRandomElement";
import {pickPlayerColor} from "./pickPlayerColor";
import {getRankSkinLeague} from "../../configs/rankSkins";

/**
 * Приоритетные цвета фракции с учетом выбранного скина
 * (зеркало серверной getFactionColorsForSkin из server/index.js).
 */
const getFactionColorsForSkin = (factionId, selectedSkinId) => {
  const faction = FACTION_CONFIGS_IMPLEMENTED.find((factionConfig) => factionConfig.id === factionId);
  if (!faction) return [];

  const factionColors = faction.colors || [];

  if (!selectedSkinId || selectedSkinId === `${factionId}_default`
    || getRankSkinLeague(selectedSkinId)
  ) {
    return factionColors;
  }

  const skinConfig = ALL_FACTION_SKINS.find((skin) => skin.id === selectedSkinId);
  const skinColors = skinConfig?.colors || [];
  if (skinColors.length === 0) {
    return factionColors;
  }

  return [...skinColors, ...factionColors];
};

/**
 * Переопределение цветов игроков локальной игры на приоритетные цвета их фракций
 * (аналог серверного алгоритма старта онлайн-игры):
 * 1) игроки обрабатываются в порядке цветов из конфигурации поля (playerColors);
 * 2) фракция без явного выбора разрешается случайной ДО назначения цвета,
 *    чтобы цвет соответствовал итоговой фракции;
 * 3) цвет игроку выдает общий с сервером pickPlayerColor: первый свободный и
 *    не похожий на занятые цвет из приоритетов фракции/скина, иначе случайный
 *    из оставшихся;
 * 4) цвета в ячейках поля (токены вида central_quarter_<color>) заменяются
 *    по построенному соответствию "цвет поля -> цвет фракции".
 *
 * Возвращает {coloredPlayers, coloredFieldData}.
 */
export const applyFactionColorsToLocalGame = (
  players, fieldData, factionLoadouts = {}, unitSkinIds = [], forceUnitSkins = false,
  // Пул для нерезолвленной «случайной» фракции: обычно фракции уже выбраны на
  // HotSeatPlayersInitPage (тоже только из доступных пользователю), это
  // страховка для путей в обход нее (авто-загрузка тестового поля)
  randomFactionIdsPool = FACTION_IDS_IMPLEMENTED
) => {
  const playerColors = fieldData?.playerColors || [];
  if (players.length === 0 || playerColors.length === 0) {
    return {coloredPlayers: players, coloredFieldData: fieldData};
  }

  // Порядок как в конфигурации поля — приоритет выбора цвета
  const orderedPlayers = [...players].sort(
    (a, b) => playerColors.indexOf(a.color) - playerColors.indexOf(b.color)
  );

  const usedColors = new Set();
  const colorMapping = {};

  const coloredPlayers = orderedPlayers.map((player) => {
    const fieldColor = player.color;
    const effectiveFactionId = player.factionId || getRandomElement(randomFactionIdsPool);
    const loadoutSkinId = factionLoadouts?.[effectiveFactionId]?.selectedSkin;
    const selectedSkinId = player.selectedSkin || loadoutSkinId || `${effectiveFactionId}_default`;

    const factionColors = getFactionColorsForSkin(effectiveFactionId, selectedSkinId);

    // Приоритеты скина/фракции, конфликты и похожие цвета — см. pickPlayerColor
    const color = pickPlayerColor(factionColors, usedColors, fieldColor);
    usedColors.add(color);
    colorMapping[fieldColor] = color;

    // Локальная игра — единственный реальный пользователь на устройстве,
    // поэтому открытость фракционного скина отрядов определяется его
    // инвентарем для ВСЕХ игроков партии независимо от того, кто за них играет.
    // Исключение: режим зрителя для админа — показываем все скины для витрины,
    // даже если сам админ их не покупал
    const unitSkinConfig = FACTION_UNIT_SKIN_CONFIGS.find((config) => config.factionId === effectiveFactionId);
    const hasUnitSkin = Boolean(unitSkinConfig)
      && (forceUnitSkins || unitSkinIds.includes(unitSkinConfig.id));

    return {
      ...player,
      color,
      factionId: effectiveFactionId,
      selectedSkin: selectedSkinId,
      hasUnitSkin,
    };
  });

  const coloredHexes = replaceColorsInObject({...fieldData.hexes}, colorMapping);

  return {
    coloredPlayers,
    coloredFieldData: {
      ...fieldData,
      hexes: coloredHexes,
    },
  };
};
